import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, Typography, Box, Divider, Grid, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { leaveBalances } from '../../services/api';
import SkeletonBlock from '../ui/SkeletonBlock';
import ErrorBanner from '../ui/ErrorBanner';
import EmptyState from '../ui/EmptyState';

const LeaveBalanceWidget = ({ year, compact = false }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const data = await leaveBalances.getMine(year);
        if (!mounted) return;
        setItems(Array.isArray(data) ? data : (data?.items || []));
      } catch (e) {
        if (!mounted) return;
        setError(e);
      } finally {
        if (!mounted) return; setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [year]);

  const alRows = useMemo(() => (items || []).filter(x => (x.code || x.Code) === 'AL'), [items]);

  return (
    <Card sx={{
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 3,
      overflow: 'hidden',
      height: compact ? '100%' : 'auto'
    }}>
      <Box sx={{
        background: (theme) => theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #2a2d3a 0%, #1e1e2e 100%)'
          : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        p: compact ? 1.5 : 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant={compact ? 'h6' : 'h5'} sx={{ fontWeight: 700, color: 'text.primary' }}>
          {t('dashboard.leaveBalance.title')}
        </Typography>
        {!compact && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Annual leave overview for current year
          </Typography>
        )}
      </Box>
      <CardContent sx={{ p: compact ? 1.5 : 3 }}>

        {loading && <SkeletonBlock lines={compact ? 2 : 4} />}
        {error && !loading && <ErrorBanner message={t('dashboard.leaveBalance.error')} />}
        {!loading && !error && alRows.length === 0 && (
          <EmptyState title={t('dashboard.leaveBalance.empty')} />
        )}

        {!loading && !error && alRows.length > 0 && (() => {
          const r = alRows[0] || {};
          const entitled = r.entitledDays ?? r.EntitledDays ?? 0;
          const used = r.usedDays ?? r.UsedDays ?? 0;
          const carried = r.carriedOverDays ?? r.CarriedOverDays ?? 0;
          const reserved = r.reservedDays ?? r.ReservedDays ?? 0;
          const available = r.availableNow ?? r.AvailableNow ?? (entitled + carried - used - reserved);

          const Stat = ({ value, label, color, emphasize }) => (
            <Box sx={{
              p: compact ? 1.5 : 3,
              borderRadius: 2,
              textAlign: 'center',
              bgcolor: (theme)=> emphasize
                ? (theme.palette.mode === 'dark' ? 'action.selected' : '#f8fafc')
                : 'background.default',
              border: (theme)=> emphasize
                ? `2px solid ${theme.palette.primary.main}`
                : `1px solid ${theme.palette.divider}`,
              boxShadow: emphasize ? '0 2px 8px rgba(25, 118, 210, 0.15)' : '0 1px 3px rgba(0,0,0,0.05)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: emphasize ? '0 8px 25px rgba(0,0,0,0.15)' : '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'translateY(-2px)'
              }
            }}>
              <Typography variant={compact ? 'h4' : 'h3'} sx={{
                fontWeight: 700,
                color: emphasize ? 'primary.main' : (color || 'text.primary'),
                lineHeight: 1,
                mb: 0.5
              }}>
                {value}
              </Typography>
              <Typography variant="body2" sx={{
                fontWeight: 500,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: compact ? '0.65rem' : '0.75rem'
              }}>
                {label}
              </Typography>
            </Box>
          );

          // Compact mode: vertical stack with just Available hero + small stats
          if (compact) {
            return (
              <Stack spacing={1.5}>
                {/* Hero: Available */}
                <Box sx={{
                  p: 2,
                  borderRadius: 2,
                  textAlign: 'center',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'action.selected' : '#f8fafc',
                  border: (theme) => `2px solid ${theme.palette.primary.main}`,
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)'
                }}>
                  <Typography variant="h2" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1 }}>
                    {available}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', mt: 0.5 }}>
                    {t('dashboard.leaveBalance.columns.available')}
                  </Typography>
                </Box>
                {/* Mini stats grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{entitled}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('dashboard.leaveBalance.columns.entitled')}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{used}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('dashboard.leaveBalance.columns.used')}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{carried}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('dashboard.leaveBalance.columns.carried')}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{reserved}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('dashboard.leaveBalance.columns.reserved')}</Typography>
                  </Box>
                </Box>
              </Stack>
            );
          }

          return (
            <>
              <Grid container spacing={2} alignItems="stretch">
                {/* Available - Hero metric */}
                <Grid xs={12} sm={6} md={2.4} order={{ xs: 1, md: 1 }}>
                  <Stat value={available} label={t('dashboard.leaveBalance.columns.available')} emphasize />
                </Grid>
                <Grid xs={12} sm={6} md={2.4} order={{ xs: 2, md: 2 }}>
                  <Stat value={entitled} label={t('dashboard.leaveBalance.columns.entitled')} />
                </Grid>
                <Grid xs={12} sm={6} md={2.4} order={{ xs: 4, md: 3 }}>
                  <Stat value={used} label={t('dashboard.leaveBalance.columns.used')} />
                </Grid>
                <Grid xs={12} sm={6} md={2.4} order={{ xs: 5, md: 4 }}>
                  <Stat value={carried} label={t('dashboard.leaveBalance.columns.carried')} />
                </Grid>
                <Grid xs={12} sm={6} md={2.4} order={{ xs: 6, md: 5 }}>
                  <Stat value={reserved} label={t('dashboard.leaveBalance.columns.reserved')} />
                </Grid>
              </Grid>

              {/* Legend with formula and explanations */}
              <Box sx={{
                mt: 2,
                p: 2,
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200'
              }}>
                <Typography variant="caption" sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  display: 'block',
                  mb: 1,
                  lineHeight: 1.3
                }}>
                  <b>Available</b> = Entitled + Carried over − Used − Reserved
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
                    <b>{t('dashboard.leaveBalance.columns.entitled')}:</b> {t('dashboard.leaveBalance.legend.entitled')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
                    <b>{t('dashboard.leaveBalance.columns.used')}:</b> {t('dashboard.leaveBalance.legend.used')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
                    <b>{t('dashboard.leaveBalance.columns.carried')}:</b> {t('dashboard.leaveBalance.legend.carried')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
                    <b>{t('dashboard.leaveBalance.columns.reserved')}:</b> {t('dashboard.leaveBalance.legend.reserved')}
                  </Typography>
                </Stack>
              </Box>
            </>
          );
        })()}
      </CardContent>
    </Card>
  );
};

export default LeaveBalanceWidget;

